import { useMemo, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'reactstrap';
import { useSort, SortTh } from '../ui/sortable';
import { formatCustomerCode, type PickListCustomer } from '../../api/pickLists';

/**
 * SP-6j — chiave del filtro: tupla `(customer_code, recipient_code)`. `null`
 * = nessuna selezione → tutto visibile (default all'apertura).
 */
export type CustomerSelection = { customer_code: string; recipient_code: number | null } | null;

export interface PickListCustomerFilterProps {
  customers: PickListCustomer[];
  selected: CustomerSelection;
  onChange: (next: CustomerSelection) => void;
  /**
   * SP-8d — bottoni azione (Assegna lista / Split lista) resi sulla stessa
   * riga di "Mostra tutti i clienti". Forniti dal pannello dettaglio.
   */
  actions?: ReactNode;
  /**
   * SP-8d — pannello espandibile (es. "Assegna lista") reso SOPRA la tabella
   * clienti. Forniti dal pannello dettaglio.
   */
  panel?: ReactNode;
}

const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

function sameKey(a: CustomerSelection, c: PickListCustomer): boolean {
  return a !== null
      && a.customer_code === c.customer_code
      && (a.recipient_code ?? null) === (c.recipient_code ?? null);
}

/**
 * SP-6b/6c/6j — sezione "Clienti nella lista" nel dettaglio pick-list.
 *
 * **SP-6j cambiamento di comportamento:** il filtro è ora un **radio singolo**
 * sulla coppia `(customer_code, recipient_code)`. Default = `null` → tutti
 * gli articoli visibili sotto. Click sulla riga = selezione; bottone
 * "Mostra tutti i clienti" = reset a `null`. Persistenza con i filtri testo:
 * la selezione **resta** anche se la riga viene filtrata via dalla tabella
 * (sotto continuano a vedersi gli articoli del cliente selezionato).
 */
export function PickListCustomerFilter({
  customers, selected, onChange, actions, panel,
}: PickListCustomerFilterProps) {
  const { t } = useTranslation(['picklist', 'common']);
  // SP-8b — i filtri testo Cliente (codice/destinatario/ragione sociale) sono
  // ora GLOBALI nella sidebar (PickListHeadFilters) e filtrano la lista delle
  // pick-list. Qui resta la tabella clienti + radio (selezione → filtra gli
  // articoli della lista aperta); mostra tutti i clienti, solo ordinati.
  const sort = useSort('recipient_code', 'asc');

  const visible = useMemo(
    () => sort.sortRows(customers, (c, col) => (c as unknown as Record<string, unknown>)[col]),
    [customers, sort],
  );

  /**
   * Totali: se c'è una selezione, riga selezionata; altrimenti somma di TUTTI
   * i clienti della lista. Coerente con la nuova default "tutto visibile".
   */
  const totals = useMemo(() => {
    const source = selected === null
      ? customers
      : customers.filter((c) => sameKey(selected, c));
    return source.reduce(
      (acc, c) => {
        acc.value += c.delivered_value;
        acc.copies += c.delivered_copies;
        acc.booked += c.booked_copies;
        return acc;
      },
      { value: 0, copies: 0, booked: 0 },
    );
  }, [customers, selected]);

  function selectRow(c: PickListCustomer): void {
    // SP-8e — secondo click sulla riga già selezionata = deseleziona (torna a
    // "tutti i clienti visibili", come il pulsante "Mostra tutti i clienti").
    if (sameKey(selected, c)) {
      onChange(null);
      return;
    }
    onChange({ customer_code: c.customer_code, recipient_code: c.recipient_code ?? null });
  }

  function onRowKey(e: KeyboardEvent<HTMLTableRowElement>, c: PickListCustomer): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectRow(c);
    }
  }

  if (customers.length === 0) {
    return (
      <section className="pl-acc-section pl-acc-section--customers">
        <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
          <h2 className="vp-label mb-0">{t('customers.title')}</h2>
          {actions && <div className="d-flex flex-wrap gap-2">{actions}</div>}
        </div>
        {panel}
        <p className="text-muted mb-0">{t('customers.empty')}</p>
      </section>
    );
  }

  const totalsScopeLabel = selected === null ? t('customers.totals_list') : t('customers.totals_selected');

  return (
    <section className="pl-acc-section pl-acc-section--customers">
      {/* SP-8g §5/§6 — rimosso il bottone "Mostra tutti i clienti" (la
          deselezione avviene con il secondo click sulla riga, SP-8e); i
          bottoni Assegna/Split (actions) sono a SINISTRA, accanto al titolo. */}
      <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
        <h2 className="vp-label mb-0">{t('customers.title_count', { count: customers.length })}</h2>
        {actions && <div className="d-flex flex-wrap gap-2">{actions}</div>}
      </div>
      {panel}

      {/* SP-8b: i 3 filtri (Codice Cliente/Destinatario/Ragione sociale) sono
          ora filtri GLOBALI nella sidebar (PickListHeadFilters) e filtrano la
          lista delle pick-list. */}

      <div className="table-responsive">
      <Table className="table-hover align-middle table-striped mb-2">
        <thead>
          <tr>
            <th style={{ width: 36 }} aria-label={t('customers.selection_aria')} />
            {/* SP-8g §4 — "Dest." stretto (valore = max 2 cifre) + tooltip "Destinatario". */}
            <SortTh label={t('customers.recipient')} title={t('customers.recipient_tooltip')} className="vp-col-dest"
              sortKey="recipient_code" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
            <SortTh label={t('customers.customer_code')} sortKey="customer_code" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
            <SortTh label={t('customers.customer_name')} sortKey="customer_name" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
            <SortTh label={t('customers.province')} sortKey="province" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
            <SortTh label={t('customers.city')} sortKey="city" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
            <th className="text-end">{t('customers.delivered_value')}</th>
            <th className="text-end">{t('customers.delivered_copies')}</th>
            <th className="text-end">{t('customers.booked_copies')}</th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 ? (
            <tr><td colSpan={9} className="text-muted">{t('customers.empty_filter')}</td></tr>
          ) : visible.map((c) => {
            const isSel = sameKey(selected, c);
            const rowKey = `${c.customer_code}-${c.recipient_code ?? 'null'}`;
            return (
              <tr key={rowKey}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSel}
                  onClick={() => selectRow(c)}
                  onKeyDown={(e) => onRowKey(e, c)}
                  className={isSel ? 'table-active border-start border-primary border-3' : ''}
                  style={{ cursor: 'pointer' }}>
                <td className="text-center">
                  <i className={`bi ${isSel ? 'bi-record-circle-fill text-primary' : 'bi-circle text-muted'}`}
                     aria-hidden="true" />
                </td>
                <td className="vp-col-dest">{c.recipient_code ?? '—'}</td>
                <td className="font-monospace vp-code">{formatCustomerCode(c.customer_code)}</td>
                <td>{c.customer_name ?? '—'}</td>
                <td>{c.province ?? '—'}</td>
                <td>{c.city ?? '—'}</td>
                <td className="text-end">{eur.format(c.delivered_value)}</td>
                <td className="text-end">{c.delivered_copies}</td>
                <td className="text-end">{c.booked_copies}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      </div>
      <p className="small text-muted mb-0">
        {totalsScopeLabel}:{' '}
        <strong>{t('customers.value_total', { value: eur.format(totals.value) })}</strong>
        {' · '}<strong>{t('customers.copies_total', { count: totals.copies })}</strong>
        {' · '}<strong>{t('customers.booked_total', { count: totals.booked })}</strong>
      </p>
    </section>
  );
}
